import { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/hooks/useAuth";
import { ItineraryData, IItineraryData } from "@/lib/builder/models/itinerary.model";
import DayBuilderStep from "@/lib/builder/DayBuilderStep";
import CheckoutStep from "@/lib/builder/CheckoutStep";
import ItinerarySidebar from "@/lib/builder/ItinerarySidebar";
import { TOTAL_STEPS } from "@/lib/builder/models/builder-config.model";
import { supabase } from "@/integrations/supabase/client";
import { showDevError } from "@/components/ui/dev-error-toast";
import { toast } from "@/hooks/useToast";
import { TravelRequestModal } from "@/lib/requests";
import { AccommodationActivityClass, ItineraryActivity } from "@/lib/builder/models/activity.model";
import { AuthContext } from "@/lib/auth/models/auth.model";
import { TravelRequest } from "@/lib/request-trip/models/travel-request.model";
import { DashboardItinerary } from "@/lib/models";
import { useModal } from "@/contexts/ModalContext";
import { createClient } from "@supabase/supabase-js";
import { SUPABASE_KEY, SUPABASE_URL } from "@/components/utils/constants";
import { RequestChangesModal } from "@/lib/builder/modals/RequestChangesModal";
import { ItineraryStepModal } from "@/lib/builder/modals/ItineraryStepModal";
import { resolveBuilderCapabilities } from "@/lib/builder/capabilities/builder-capabilities";
import { BuilderCapabilitiesProvider } from "@/lib/builder/capabilities/BuilderCapabilitiesContext";
import { useItineraries } from "@/hooks/useItineraries";
import { useTravelRequests } from "@/hooks/useTravelRequests";
import { createCheckInOutActivities } from "@/components/utils/createCheckOutFromCheckIn";
import BuilderLayout from "@/components/layout/builder/BuilderLayout";
import { getRandomUnsplashPhoto, trackUnsplashDownload, unsplashPhotoToMediaItem } from "@/services/unsplash.service";
import { hasCommittedMoney, hasCollectedMoney, isItinerarySettled } from "@/lib/builder/utils/itinerary-lock";
import { hasPaymentInFlight } from "@/lib/builder/utils/payment-plan.utils";
import { PricingLockBanner } from "@/lib/builder/components/PricingLockBanner";
import { usePaymentPlanQuery } from "@/lib/builder/hooks/usePaymentPlan";

const ItineraryBuilder = () => {
    const {
      userData,
      logoSmallUrl,
    } = useAuth();
    // Ref per comunicare date coinvolte dal drag-drop a handleDataChange
    // senza cambiare la firma di onDataChange
    const affectedDatesRef = useRef<string[] | null>(null);

    // Funzione helper per salvataggio attività con diff
    const saveActivitiesWithDiff = async (
      itineraryId: string,
      builderActivities: ItineraryActivity[],
      filterDates?: string[],
      itineraryTotals?: Record<string, number>
    ): Promise<{ success: boolean; insertIds: string[]; updateIds: string[] }> => {
      // Recupera le attività già salvate a DB
      const dbActivities = await fetchItineraryActivities(itineraryId);
      // Calcola le differenze (scoped alle sole date indicate, se fornite)
      const { diffActivities } = await import("@/lib/builder/utils/diffActivities");
      const { toInsert, toUpdate, toDelete } = diffActivities(builderActivities, dbActivities, filterDates);
      const insertIds = toInsert.map(a => a.id).filter(Boolean) as string[];
      const updateIds = toUpdate.map(a => a.id).filter(Boolean) as string[];
      // Salva con la nuova interfaccia
      const success = await saveItineraryActivities(itineraryId, toInsert, toUpdate, toDelete, itineraryTotals);
      return { success: !!success, insertIds, updateIds };
    };
  const { t } = useI18n();
  const { openModal } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestId = searchParams.get('requestId');
  const itineraryId = searchParams.get('itineraryId');
  const stepFromUrl = searchParams.get('step');


  // Funzioni specifiche per l'ItineraryBuilder (senza caricare tutti i dati)
  const { saveItinerary, fetchItineraryActivities, saveItineraryActivities, updateItineraryStatus } = useItineraries({ disableAutoFetch: true });
  const { fetchTravelRequest } = useTravelRequests({ disableAutoFetch: true });

  // Get user role from AuthContext
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error('useItineraries deve essere usato all\'interno di un AuthProvider');
  }
  const userRole = authContext.userRole;

  // Step navigation state - Initialize from URL if present
  const [currentStep, setCurrentStep] = useState(() => {
    return stepFromUrl ? parseInt(stepFromUrl) : 1;
  });
  const [canProceed, setCanProceed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingAuto, setIsSavingAuto] = useState(false);
  const [failedActivityIds, setFailedActivityIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isTravelRequestModalOpen, setIsTravelRequestModalOpen] = useState(false);
  const [isItineraryModalOpen, setIsItineraryModalOpen] = useState(false);
  const [isRequestChangesModalOpen, setIsRequestChangesModalOpen] = useState(false);
  const [itineraryBuilder, setItineraryBuilder] = useState<any>(null);

  // Form data state - Initialize with empty data
  const [itineraryData, setItineraryData] = useState<ItineraryData>();

  // Un template non ha checkout: nessuna richiesta di viaggio collegata, quindi niente
  // preventivo da inviare e niente piano rate da comporre. Resta il solo Day Builder.
  const isTemplate = !!itineraryData?.isTemplate;

  // Tutto cio' che il builder mostra o permette in questo flusso, calcolato una volta e
  // distribuito via Context: i componenti non guardano piu' il ruolo per conto proprio.
  const capabilities = useMemo(
    () =>
      resolveBuilderCapabilities({
        entry: 'workspace',
        role: userRole,
        isTemplate,
        itineraryStatus: itineraryData?.status,
        hasTravelRequest: !!itineraryData?.travel_request_id,
      }),
    [userRole, isTemplate, itineraryData?.status, itineraryData?.travel_request_id],
  );
  const isReadOnly = !capabilities.canEdit;

  // Il cliente ha gia' messo del denaro sul viaggio.
  //
  // Non disabilita niente: prezzi, costi delle attivita' e piano rate restano modificabili
  // anche a pagamento avvenuto — un viaggio pagato in parte continua a cambiare, e il TD deve
  // poterlo riprezzare. Serve solo a scegliere cosa dice il banner in cima al builder, che da
  // qui in poi ricorda l'unico adempimento vero: riallineare le rate al nuovo totale.
  const { data: builderPaymentPlan, isLoading: isPaymentPlanLoading } = usePaymentPlanQuery(
    userRole !== 'traveler' ? itineraryData?.id : undefined
  );
  const hasCommittedPayments = hasCommittedMoney(builderPaymentPlan);
  // Viaggio saldato: cambia solo il messaggio, non cosa e' disabilitato. Si aspetta il
  // piano prima di dirlo, altrimenti su un viaggio col solo acconto versato lo status
  // 'completed' farebbe lampeggiare "viaggio completato" per il tempo della query.
  const isItineraryCompleted =
    !isPaymentPlanLoading &&
    userRole !== 'admin' &&
    isItinerarySettled(builderPaymentPlan, itineraryData?.status);

  const changeStep = useCallback((step: number, resetDate: boolean = false) => {
    setCurrentStep(step);
    if (resetDate && step === 2 && itineraryData) {
      setSelectedDate(itineraryData.firstDay);
    }
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('step', step.toString());
      if (resetDate && step === 2 && itineraryData) {
        newParams.set('date', itineraryData.firstDay);
      }
      return newParams;
    });
  }, [itineraryData, setSearchParams]);

  // Ref per stabilizzare changeStep nelle funzioni di inizializzazione
  const changeStepRef = useRef(changeStep);
  changeStepRef.current = changeStep;

  // Ref per avere sempre l'ultimo itineraryData nelle callback async
  const itineraryDataRef = useRef<ItineraryData | undefined>(itineraryData);
  useEffect(() => { itineraryDataRef.current = itineraryData; }, [itineraryData]);

  // Main function to initialize itinerary data
  const initializeItineraryData = useCallback(async (requestId: string) => {
    setIsLoading(true);
    try {
      // 1. Fetch travel request
      const travelRequest = await fetchTravelRequest(requestId);

      if (!travelRequest) {
        return;
      }

      const itineraryData = new ItineraryData({} as IItineraryData);
      itineraryData.fetchTravelRequest(travelRequest);

      // Placeholder cover casuale (Unsplash) così l'itinerario non parte con la copertina vuota.
      // Non bloccante: se Unsplash non risponde, l'itinerario si crea comunque senza immagine.
      try {
        const photo = await getRandomUnsplashPhoto({ query: itineraryData.title });
        // La cover è un MediaItem: conserva l'attribuzione, mostrata anche al viaggiatore.
        itineraryData.image = unsplashPhotoToMediaItem(photo);
        // Assegnare la foto all'itinerario è un "uso" ai fini delle Unsplash API
        // Guidelines, quindi va notificato come una selezione manuale.
        void trackUnsplashDownload(photo.downloadLocation);
      } catch (error) {
        console.error('Error fetching Unsplash placeholder photo:', error);
      }

      // Persist immediately so we always have an ID before the user starts adding activities
      const savedItinerary = await saveItinerary(itineraryData);
      const finalData = savedItinerary
        ? itineraryData.update({ id: savedItinerary.id })
        : itineraryData;

      if (savedItinerary) {
        setSearchParams(prev => {
          const newParams = new URLSearchParams(prev);
          newParams.set('itineraryId', savedItinerary.id);
          newParams.delete('requestId');
          return newParams;
        });
      }

      setItineraryData(finalData);
      setSelectedDate(finalData.firstDay);
      changeStepRef.current(1, true);

    } catch (error) {
      console.error('Error initializing itinerary data:', error);
      toast({
        title: t('builder.initializationError'),
        description: t('builder.initializationErrorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [fetchTravelRequest]);

  // Function to initialize from existing itinerary ID
  const initializeFromItineraryId = useCallback(async (itineraryId: string) => {
    setIsLoading(true);
    try {
      // Fetch the itinerary from database
      const { data: itinerary, error: itineraryError } = await supabase
        .from('itineraries')
        .select('*')
        .eq('id', itineraryId)
        .single();

      if (itineraryError) {
        console.error('Error fetching itinerary by ID:', itineraryError);
        showDevError({ error: itineraryError, context: 'fetching itinerary by ID' });
        toast({
          title: t('builder.loadError'),
          description: t('builder.loadErrorDesc'),
          variant: "destructive",
        });
        return;
      }

      // Fetch the travel request only if it exists (not orphan itinerary)
      let travelRequest = null;
      if (itinerary.travel_request_id) {
        travelRequest = await fetchTravelRequest(itinerary.travel_request_id);
        if (!travelRequest) {
          return;
        }
      }

      // Fetch activities
      const activities: ItineraryActivity[] = await fetchItineraryActivities(itineraryId);

      // Create ItineraryData with existing data
      const itineraryData = new ItineraryData(itinerary as IItineraryData);

      // per ogni attività di accommodation modifico il vettore attivities andando a creare le accomodation checkout per ogni accommodation
      const modifiedActivities: ItineraryActivity[] = activities.flatMap((activity: ItineraryActivity) => {
        if (activity instanceof AccommodationActivityClass) {
          // createCheckInOutActivities returns two entries (check-in, check-out)
          const [checkIn, checkOut] = createCheckInOutActivities(activity as AccommodationActivityClass);
          return [checkIn, checkOut];
        }
        return activity as ItineraryActivity;
      });

      // Set travel request only if it exists
      if (travelRequest) {
        itineraryData.travel_request = new TravelRequest(travelRequest);
      }
      itineraryData.setItineraryDays(modifiedActivities as ItineraryActivity[]);

      setItineraryData(itineraryData);
      setSelectedDate(itineraryData.firstDay);

    } catch (error) {
      console.error('Error initializing from itinerary ID:', error);
      toast({
        title: t('builder.initializationError'),
        description: t('builder.loadItineraryErrorDesc'),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [fetchTravelRequest, fetchItineraryActivities, userRole]);

  // Ref per stabilizzare le funzioni di inizializzazione
  const initializeItineraryDataRef = useRef(initializeItineraryData);
  initializeItineraryDataRef.current = initializeItineraryData;

  const initializeFromItineraryIdRef = useRef(initializeFromItineraryId);
  initializeFromItineraryIdRef.current = initializeFromItineraryId;

  // Il totale dei costi servizi (voli inclusi), la stessa base che il checkout usa per
  // il markup. Sul template e' l'unico numero economico visibile, e sta nel footer.
  const templateServicesTotal = useMemo(() => {
    if (!isTemplate || !itineraryData) return undefined;
    const totals = itineraryData.extractTotals();
    return (totals.commisionable_total || 0) + (totals.not_commisionable_total || 0);
  }, [isTemplate, itineraryData]);

  // Un vecchio link con ?step=2 su un template, o aperto dal viaggiatore, atterrerebbe
  // su un checkout che in quel flusso non esiste.
  // Si aspetta il caricamento: prima di avere l'itinerario isTemplate e' ancora false.
  useEffect(() => {
    if (!isLoading && !capabilities.hasCheckout && currentStep !== 1) {
      changeStep(1);
    }
  }, [isLoading, capabilities.hasCheckout, currentStep, changeStep]);

  // Step navigation handlers
  const handleNextStep = async () => {
    if (!capabilities.hasCheckout) return;
    if (currentStep < TOTAL_STEPS && itineraryData) {
      const newStep = currentStep + 1;
      changeStep(newStep, true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep <= 1) return;
    const newStep = currentStep - 1;

    changeStep(newStep);
  };

  // Auto-save triggered after every activity action (add/edit/delete/duplicate/drag-drop)
  const autoSave = useCallback(async (updatedData: ItineraryData, filterDates?: string[]) => {
    if (isReadOnly) return;
    const id = updatedData.id || itineraryId;
    if (!id) return;
    setIsSavingAuto(true);
    try {
      const builderActivities = updatedData.extractAllActivities();
      const itineraryTotals = updatedData.getTotalsPayload();
      const { success, insertIds, updateIds } = await saveActivitiesWithDiff(id, builderActivities, filterDates, itineraryTotals);
      if (success) {
        // Rimuove dai failed tutti gli id appena salvati con successo
        setFailedActivityIds(prev => {
          if (prev.size === 0) return prev;
          const next = new Set(prev);
          [...insertIds, ...updateIds].forEach(id => next.delete(id));
          return next;
        });
      } else {
        // Marca come failed SOLO le attività mai salvate prima (toInsert)
        // Le toUpdate erano già in DB → la loro ultima versione salvata è al sicuro
        setFailedActivityIds(prev => {
          if (insertIds.length === 0) return prev;
          const next = new Set(prev);
          insertIds.forEach(id => next.add(id));
          return next;
        });
      }
    } catch (error) {
      console.error('Error auto-saving activities:', error);
    } finally {
      setIsSavingAuto(false);
    }
  }, [isReadOnly, itineraryId, saveActivitiesWithDiff]);

  const handleRetry = useCallback(() => {
    if (itineraryDataRef.current) autoSave(itineraryDataRef.current);
  }, [autoSave]);

  const handleDataChange = (newData: Partial<IItineraryData>) => {
    if (isReadOnly) return;
    setItineraryData((prev: ItineraryData) => prev.update(newData));
    if ('itinerary_days' in newData && itineraryDataRef.current) {
      const updated = itineraryDataRef.current.update(newData);
      // Se affectedDatesRef è valorizzato, si tratta di un reorder: usiamo il diff scoped
      const filterDates = affectedDatesRef.current ?? undefined;
      affectedDatesRef.current = null;
      autoSave(updated, filterDates);
    }
    if (('image' in newData || 'cancellation_policy_notes' in newData || 'p2b_commission_percentage' in newData) && itineraryDataRef.current) {
      const updated = itineraryDataRef.current.update(newData);
      saveItinerary(updated);
    }
  };

  const getSyncedItineraryData = useCallback((baseData: ItineraryData) => {
    if (!itineraryBuilder) {
      return baseData;
    }

    return baseData.update({
      itinerary_days: itineraryBuilder.toArray()
    });
  }, [itineraryBuilder]);

  const handleExit = () => {
    exitBuilder();
  };

  const exitBuilder = () => {
    navigate('/dashboard');
  };

  const sendItineraryForReview = async () => {
    if (!itineraryData) return;

    // Ensure itinerary is saved before sending
    if (!itineraryData.id) {
      toast({
        title: t('itinerary.saveBeforeSending'),
        description: t('itinerary.saveBeforeSendingDesc'),
        variant: "destructive",
      });
      return;
    }

    // Map to DashboardItinerary and open send-quote modal
    const mappedItinerary = mapToDashboardItinerary(itineraryData);
    openModal('send-quote', {
      itinerary: mappedItinerary,
      onSuccessClose: () => navigate('/dashboard'),
    });
  };

  // Removed: handleSendQuote is now handled directly by SendQuoteModal via itinerary.service

  const handleCancel = () => {
    exitBuilder();
  };

  // Maps ItineraryData to a DashboardItinerary-compatible shape for the preview modal
  const mapToDashboardItinerary = (data: ItineraryData): DashboardItinerary => {
    const tr = data.travel_request;
    return {
      id: data.id || '',
      travelRequestId: data.travel_request_id || '',
      title: data.title,
      status: data.status || 'draft',
      startDate: data.start_date || null,
      endDate: data.end_date || null,
      guestsData: data.guests_data || [],
      budget: data.budget || 0,
      description: data.description || '',
      image: data.image || null,
      note: data.note || null,
      createdAt: '',
      updatedAt: '',
      total: data.total ?? null,
      commisionableTotal: data.commisionable_total ?? null,
      notCommisionableTotal: data.not_commisionable_total ?? null,
      p2bCommissionPercentage: data.p2b_commission_percentage ?? null,
      p2bCommissionAmount: data.p2b_commission_amount ?? null,
      tdFeePercentage: data.td_fee_percentage ?? null,
      contactEmail: tr?.contact_email || '',
      travelRequest: {
        id: tr?.id || '',
        userId: tr?.user_id || null,
        destinations: tr?.destinations || [],
        departurePoint: tr?.departure_point || null,
        needsTransportation: tr?.needs_transportation || false,
        totalGuests: tr?.total_guests || data.guests_data?.length || 1,
        guestsData: (tr?.guests_data as any[]) || data.guests_data || [],
        travelDatesFlexible: tr?.travel_dates_flexible || false,
        travelStartDate: tr?.travel_start_date || data.start_date || null,
        travelEndDate: tr?.travel_end_date || data.end_date || null,
        budgetMin: tr?.budget_min ?? null,
        budgetMax: tr?.budget_max ?? null,
        budgetFlexible: tr?.budget_flexible || false,
        interests: tr?.interests || [],
        additionalNotes: tr?.additional_notes || null,
        contact: {
          firstName: tr?.contact_first_name || '',
          lastName: tr?.contact_last_name || '',
          email: tr?.contact_email || '',
          phone: tr?.contact_phone || '',
        },
        status: (tr?.status as any) || 'assigned',
        assignedAdvisorId: tr?.assigned_designer_id || null,
        preferredAdvisorId: tr?.travel_designer_preference || null,
        createdAt: tr?.created_at || '',
        updatedAt: tr?.updated_at || '',
      },
      assignedAdvisor: null,
      preferredAdvisor: null,
    } as unknown as DashboardItinerary;
  };

  // Sidebar handlers
  const handleEditTrip = () => {
    if (isReadOnly) {
      openModal('itinerary-details', {
        itinerary: mapToDashboardItinerary(itineraryData),
        onViewItinerary: () => {},
        showButton: false,
      });
    } else {
      setIsItineraryModalOpen(true);
    }
  };

  const handleItineraryModalClose = () => {
    if (!isReadOnly) {
      setItineraryData((prev: ItineraryData) => prev.updateItineraryDays());
    }
    setIsItineraryModalOpen(false);
  };

  const handleDaySelect = (date: string) => {
    setSelectedDate(date);
    // Update URL with selected date
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('date', date);
      return newParams;
    });
  };

  useEffect(() => {
    if (itineraryId) {
      // If we have an itineraryId, load the existing itinerary
      initializeFromItineraryIdRef.current(itineraryId);
    } else if (requestId) {
      // If we have a requestId, try to load existing or create new
      initializeItineraryDataRef.current(requestId);
    }
  }, [requestId, itineraryId]);

  const handleViewTripRequest = () => {
    setIsTravelRequestModalOpen(true);
  };

  // Chiamato da DayBuilderStep prima che updateBuilder triggeri onDataChange,
  // registra le date coinvolte così autoSave può limitare il diff a quelle sole date
  const handleActivityReorder = useCallback((affectedDates: string[]) => {
    affectedDatesRef.current = affectedDates;
  }, []);

  const handleRequestChanges = () => {
    exitBuilder();
  };


  // Set canProceed based on current step and data completeness
  useEffect(() => {
    // For step 1 (DayBuilderStep), canProceed is managed by DayBuilderStep via setCanProceed
    // For step 2 (Checkout), canProceed is always true
    if (currentStep === 2) {
      setCanProceed(true);
    }
  }, [itineraryData, currentStep, isLoading]);

  // Handler approva/rigetta (solo per admin quando itinerario è in pending)
  const handleAcceptItinerary = async (note?: string) => {
    if (!itineraryData?.id) return;
    try {
      await updateItineraryStatus(itineraryData.id, 'approved', note);
      exitBuilder();
    } catch (error) {
      console.error('Error accepting itinerary:', error);
      toast({
        title: t('builder.errorAccepting'),
        description: t('builder.errorAcceptingDesc'),
        variant: "destructive",
      });
    }
  };

  const handleRejectItinerary = async (note?: string) => {
    if (!itineraryData?.id) return;
    try {
      await updateItineraryStatus(itineraryData.id, 'rejected', note);
      exitBuilder();
    } catch (error) {
      console.error('Error rejecting itinerary:', error);
      toast({
        title: t('builder.errorRejecting'),
        description: t('builder.errorRejectingDesc'),
        variant: "destructive",
      });
    }
  };

  // Get current step component
  const getCurrentStepComponent = () => {
    const effectiveSelectedDate = selectedDate || itineraryData?.firstDay || null;
    const stepProps = {
      data: itineraryData,
      onDataChange: handleDataChange,
      onNext: handleNextStep,
      onPrevious: handlePreviousStep,
      onSave: sendItineraryForReview,
      onCancel: handleCancel,
      canProceed,
      setCanProceed,
      onDaySelect: handleDaySelect,
      selectedDate: effectiveSelectedDate,
      onBuilderChange: setItineraryBuilder,
      failedActivityIds,
      onActivityReorder: handleActivityReorder,
    };

    switch (currentStep) {
      case 1:
        return <DayBuilderStep {...stepProps} />;
      case 2:
        return capabilities.hasCheckout ? <CheckoutStep {...stepProps} /> : <DayBuilderStep {...stepProps} />;
      default:
        return <DayBuilderStep {...stepProps} />;
    }
  };

  // Aggiorna la nota dell'itinerario
  const handleUpdateItineraryNote = async (note: string): Promise<void> => {
    if (!itineraryData) return;
    const updated = itineraryData.setItineraryNote(note);
    setItineraryData(updated);
    if (updated.id) {
      await saveItinerary(updated);
    }
  };

  return (
    <BuilderCapabilitiesProvider value={capabilities}>
    <BuilderLayout
      onPreviousStep={handlePreviousStep}
      onNextStep={handleNextStep}
      onSave={sendItineraryForReview}
      onExit={handleExit}
      isAutoSaving={isSavingAuto}
      hasUnsaved={failedActivityIds.size > 0}
      onRetry={handleRetry}
      onRejectItinerary={handleRejectItinerary}
      onAcceptItinerary={handleAcceptItinerary}
      onCancel={handleCancel}
      onViewTripRequest={itineraryData?.travel_request ? handleViewTripRequest : undefined}
      onRequestChanges={() => setIsRequestChangesModalOpen(true)}
      totalPrice={itineraryData?.total}
      startDate={itineraryData?.start_date}
      endDate={itineraryData?.end_date}
      refundable={itineraryData?.refundable}
      cancellationPolicyNotes={itineraryData?.cancellation_policy_notes}
      isFirstStep={currentStep === 1}
      isLastStep={currentStep === TOTAL_STEPS}
      isRecapStep={currentStep === TOTAL_STEPS}
      canProceed={canProceed}
      servicesTotal={templateServicesTotal}
      showHeader={true}
      showFooter={true}
      isLoading={isLoading}
      logoSmallUrl={logoSmallUrl ?? undefined}
      onUpdateItineraryNote={handleUpdateItineraryNote}
      itineraryStatus={itineraryData?.status}
      itineraryId={itineraryData?.id}
      travelRequestId={itineraryData?.travel_request_id}
    >
      <div className="flex h-full flex-col lg:flex-row">
        {/* Sidebar - Only visible on step 1 (Day Builder)
            On mobile we force it to appear above the main content (order-first, w-full)
            On desktop it behaves as a side column (lg:w-80, lg:order-none) */}
        {currentStep === 1 && itineraryData && (
          <div className="w-full lg:w-80 order-first lg:order-none">
            <ItinerarySidebar
              data={itineraryData}
              builder={itineraryBuilder}
              onEditTrip={handleEditTrip}
              onDaySelect={handleDaySelect}
              selectedDate={selectedDate}
            />
          </div>
        )}

        {/* Main Content - make it order-last on mobile so sidebar is shown above */}
        <div className={`flex-1 flex flex-col overflow-y-auto ${currentStep === 1 ? 'lg:ml-0' : ''} order-last lg:order-none`}>
          {/* Il viaggiatore non lo vede: i prezzi non sono mai stati suoi da modificare,
              e ritirare il preventivo non e' una sua azione. */}
          {!isLoading && capabilities.showPricing && (
            <PricingLockBanner
              itineraryId={itineraryData?.id}
              status={itineraryData?.status}
              hasCommittedPayments={hasCommittedPayments}
              isCompleted={isItineraryCompleted}
              hasPayments={hasCollectedMoney(builderPaymentPlan)}
              isPaymentInFlight={hasPaymentInFlight(builderPaymentPlan)}
              onRevoked={() =>
                setItineraryData((prev: ItineraryData) => prev.update({ status: 'draft' }))
              }
            />
          )}
          <div className="w-full flex-1 min-h-0">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                  <p className="text-gray-600">
                    {itineraryId ? t('builder.loadingExistingItinerary') : t('builder.loadingTravelRequestData')}
                  </p>
                </div>
              </div>
            ) : (
              getCurrentStepComponent()
            )}
          </div>
        </div>
      </div>

      {/* Travel Request Modal - Solo visualizzazione */}
      <TravelRequestModal
        isOpen={isTravelRequestModalOpen}
        onClose={() => setIsTravelRequestModalOpen(false)}
        travelRequest={itineraryData?.travel_request ?? null}
        showActions={false}
        enableDetailSheets={
          userRole === "admin" ||
          userRole === "agency" ||
          userRole === "travel_designer" ||
          userRole === "travel_advisor_member"
        }
      />

      {/* Request Changes Modal */}
      <RequestChangesModal
        isOpen={isRequestChangesModalOpen}
        onClose={() => setIsRequestChangesModalOpen(false)}
        onSend={handleRequestChanges}
        itineraryId={itineraryData?.id ?? ''}
      />

      {/* Itinerary Step Modal */}
      {isItineraryModalOpen && (
        <ItineraryStepModal
          onClose={handleItineraryModalClose}
          data={itineraryData}
          onDataChange={handleDataChange}
          canProceed={canProceed}
          setCanProceed={setCanProceed}
        />
      )}
    </BuilderLayout>
    </BuilderCapabilitiesProvider>
  );
};

export default ItineraryBuilder;
